import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import SharedInputText from '../../components/Shared/SharedInputText';
import SharedDropdown from '../../components/Shared/SharedDropdown';
import ItemPackagingTab from './ItemPackagingTab';
import { getERPClient } from '../../services/supplyChainApi';

const SectionHeading = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-2">
    <div className="bg-gray-100 p-1.5 rounded-full flex items-center justify-center">
      <i className={`pi ${icon} text-primary text-sm`} />
    </div>
    <h3 className="text-base font-semibold text-gray-800">{label}</h3>
  </div>
);

const ITEM_TYPE_OPTIONS = [
  { value: 'MEDICATION', label: 'MEDICATION' },
  { value: 'CONSUMABLE', label: 'CONSUMABLE' },
  { value: 'MEDICAL_DEVICE', label: 'MEDICAL_DEVICE' },
  { value: 'LAB_SUPPLY', label: 'LAB_SUPPLY' },
  { value: 'ASSET', label: 'ASSET' },
  { value: 'LINEN', label: 'LINEN' },
  { value: 'FOOD', label: 'FOOD' },
  { value: 'SURGICAL', label: 'SURGICAL' },
  { value: 'REAGENT', label: 'REAGENT' },
  { value: 'OFFICE_SUPPLY', label: 'OFFICE_SUPPLY' },
  { value: 'MAINTENANCE', label: 'MAINTENANCE' },
  { value: 'HOUSEKEEPING', label: 'HOUSEKEEPING' },
];

const ABC_CLASSIFICATION_OPTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];

const VED_CLASSIFICATION_OPTIONS = [
  { value: 'V', label: 'V - Vital' },
  { value: 'E', label: 'E - Essential' },
  { value: 'D', label: 'D - Desirable' },
];

const STORAGE_CONDITION_OPTIONS = [
  { value: 'AMBIENT', label: 'AMBIENT' },
  { value: 'COLD_2_8', label: 'COLD_2_8' },
  { value: 'FROZEN_MINUS_20', label: 'FROZEN_MINUS_20' },
  { value: 'FROZEN_MINUS_40', label: 'FROZEN_MINUS_40' },
  { value: 'FROZEN_MINUS_80', label: 'FROZEN_MINUS_80' },
  { value: 'HUMIDITY_CONTROLLED', label: 'HUMIDITY_CONTROLLED' },
  { value: 'LIGHT_SENSITIVE', label: 'LIGHT_SENSITIVE' },
  { value: 'HAZARDOUS_STORAGE', label: 'HAZARDOUS_STORAGE' },
  { value: 'CYTOTOXIC_STORAGE', label: 'CYTOTOXIC_STORAGE' },
  { value: 'NARCOTIC_VAULT', label: 'NARCOTIC_VAULT' },
];

const DEFAULT_COST_METHOD_OPTIONS = [
  { value: 'FIFO', label: 'FIFO' },
  { value: 'LIFO', label: 'LIFO' },
  { value: 'WEIGHTED_AVERAGE', label: 'WEIGHTED_AVERAGE' },
  { value: 'STANDARD', label: 'STANDARD' },
  { value: 'SPECIFIC', label: 'SPECIFIC' },
];

const PROCUREMENT_METHOD_OPTIONS = [
  { value: 'TENDER', label: 'TENDER' },
  { value: 'QUOTATION', label: 'QUOTATION' },
  { value: 'DIRECT_PURCHASE', label: 'DIRECT_PURCHASE' },
  { value: 'RATE_CONTRACT', label: 'RATE_CONTRACT' },
  { value: 'EMERGENCY', label: 'EMERGENCY' },
  { value: 'CONSIGNMENT', label: 'CONSIGNMENT' },
  { value: 'DONATION', label: 'DONATION' },
];

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export default function CreateERPItemPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(location.state?.item || null);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);

  const isEdit = !!id || !!initialData?.id;

  useEffect(() => {
    async function loadMasterData() {
      try {
        const erpClient = getERPClient();
        const [catRes, uomRes] = await Promise.allSettled([
          erpClient.getCategories(),
          erpClient.getUoms(),
        ]);
        if (catRes.status === 'fulfilled' && catRes.value) {
          const list = Array.isArray(catRes.value) ? catRes.value : (catRes.value.data || []);
          setCategories(list);
        }
        if (uomRes.status === 'fulfilled' && uomRes.value) {
          const list = Array.isArray(uomRes.value) ? uomRes.value : (uomRes.value.data || []);
          setUoms(list);
        }
      } catch (err) {
        console.error('Failed to load categories/UOMs:', err);
      }
    }
    loadMasterData();
  }, []);

  useEffect(() => {
    const targetId = id || initialData?.id;
    if (targetId) {
      async function fetchItemDetails() {
        setLoading(true);
        try {
          const erpClient = getERPClient();
          const res = await erpClient.getItem(targetId);
          const item = res.data || res;
          if (item && (item.id || item.item_code)) {
            setInitialData(item);
          }
        } catch (err) {
          console.error('Failed to load item details:', err);
          toast.error(t('Failed to load item details'));
        } finally {
          setLoading(false);
        }
      }
      fetchItemDetails();
    }
  }, [id]);

  // Packaging levels are managed by the ItemPackagingTab (separate API,
  // like the supply chain) so no inline packaging fetching is needed here.

  const categoryOptions = [
    { value: 'none', label: t('Select Category') },
    ...categories.map((c) => ({
      value: c.id,
      label: `${c.code || ''} - ${c.name || c.label || ''}`.replace(/^- /, '') || (c.code || c.id),
    })),
  ];

  const uomOptions = [
    { value: 'none', label: t('Select UOM') },
    ...uoms.map((u) => ({
      value: u.id,
      label: u.name ? `${u.code || ''} - ${u.name}`.replace(/^- /, '') : (u.code || u.id),
    })),
  ];

  const buildValues = (data) => ({
    item_code: data?.item_code || '',
    short_name: data?.short_name || '',
    name_en: data?.name_en || data?.name || '',
    name_ar: data?.name_ar || '',
    generic_name_en: data?.generic_name_en || '',
    generic_name_ar: data?.generic_name_ar || '',
    trade_name_en: data?.trade_name_en || '',
    trade_name_ar: data?.trade_name_ar || '',
    category_id: data?.category_id || 'none',
    subcategory_id: data?.subcategory_id || '',
    description_en: data?.description_en || '',
    description_ar: data?.description_ar || '',

    item_type: data?.item_type || '',
    abc_classification: data?.abc_classification || 'A',
    ved_classification: data?.ved_classification || 'V',
    therapeutic_class: data?.therapeutic_class || '',
    drug_form: data?.drug_form || '',
    route_of_administration: data?.route_of_administration || '',
    strength: data?.strength || '',
    strength_uom: data?.strength_uom || '',
    stock_uom_id: data?.stock_uom_id || 'none',

    storage_condition: data?.storage_condition || '',
    gtin_14: data?.gtin_14 || '',
    sfda_registration_no: data?.sfda_registration_no || '',
    storage_temp_min: data?.storage_temp_min ?? '',
    storage_temp_max: data?.storage_temp_max ?? '',
    is_batch_tracked: data?.is_batch_tracked !== undefined ? String(data.is_batch_tracked) : 'false',
    is_expiry_tracked: data?.is_expiry_tracked !== undefined ? String(data.is_expiry_tracked) : 'false',
    quality_test_required: data?.quality_test_required !== undefined ? String(data.quality_test_required) : 'false',
    is_active: data?.is_active !== undefined ? String(data.is_active) : 'true',

    min_order_qty: data?.min_order_qty ?? '',
    reorder_point: data?.reorder_point ?? '',
    safety_stock_qty: data?.safety_stock_qty ?? '',
    max_stock_level: data?.max_stock_level ?? '',
    procurement_method: data?.procurement_method || '',
    default_cost_method: data?.default_cost_method || '',
    standard_cost: data?.standard_cost ?? '',
    is_purchased: data?.is_purchased !== undefined ? String(data.is_purchased) : 'true',
  });

  const validationSchema = Yup.object().shape({
    item_code: Yup.string().required(t('Item Code is required')),
    name_en: Yup.string().required(t('Item Name (EN) is required')),
    name_ar: Yup.string().required(t('Item Name (AR) is required')),
    item_type: Yup.string().required(t('Item Type is required')),
    abc_classification: Yup.string().oneOf(['A', 'B', 'C'], t('Must be A, B, or C')).required(t('ABC Classification is required')),
    ved_classification: Yup.string().oneOf(['V', 'E', 'D'], t('Must be V, E, or D')).required(t('VED Classification is required')),
    stock_uom_id: Yup.string().test('stock-uom', t('Stock UOM is required'), (v) => !!v && v !== 'none'),
    gtin_14: Yup.string()
      .nullable()
      .test('gtin-len', t('GTIN-14 must be exactly 14 digits'), (v) => !v || v.length === 14)
      .test('gtin-num', t('GTIN-14 must contain only digits'), (v) => !v || /^\d+$/.test(v)),
    storage_temp_min: Yup.number().nullable().typeError(t('Must be a number')),
    storage_temp_max: Yup.number().nullable().typeError(t('Must be a number')).test(
      'temp-compare',
      t('Max temperature must be greater than or equal to min temperature'),
      function (value) {
        const { storage_temp_min } = this.parent;
        if (value === undefined || value === null || storage_temp_min === undefined || storage_temp_min === null) {
          return true;
        }
        return Number(value) >= Number(storage_temp_min);
      }
    ),
    min_order_qty: Yup.number().nullable().typeError(t('Must be a number')).min(0, t('Min order quantity cannot be negative')),
    reorder_point: Yup.number().nullable().typeError(t('Must be a number')).min(0, t('Reorder point cannot be negative')),
    safety_stock_qty: Yup.number().nullable().typeError(t('Must be a number')).min(0, t('Safety stock cannot be negative')),
    max_stock_level: Yup.number().nullable().typeError(t('Must be a number')).min(0, t('Max stock level cannot be negative')),
    standard_cost: Yup.number().nullable().typeError(t('Must be a number')).min(0, t('Standard cost cannot be negative')),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const erpClient = getERPClient();
      const payload = {
        ...values,
        name: values.name_en,
        unit_price: values.standard_cost !== '' ? Number(values.standard_cost) : 0,
        category_id: values.category_id === 'none' || !values.category_id ? null : values.category_id,
        stock_uom_id: values.stock_uom_id === 'none' || !values.stock_uom_id ? null : values.stock_uom_id,
        is_batch_tracked: values.is_batch_tracked === 'true' || values.is_batch_tracked === true,
        is_expiry_tracked: values.is_expiry_tracked === 'true' || values.is_expiry_tracked === true,
        quality_test_required: values.quality_test_required === 'true' || values.quality_test_required === true,
        is_active: values.is_active === 'true' || values.is_active === true,
        is_purchased: values.is_purchased === 'true' || values.is_purchased === true,
        storage_temp_min: values.storage_temp_min !== '' ? Number(values.storage_temp_min) : null,
        storage_temp_max: values.storage_temp_max !== '' ? Number(values.storage_temp_max) : null,
        min_order_qty: values.min_order_qty !== '' ? Number(values.min_order_qty) : null,
        reorder_point: values.reorder_point !== '' ? Number(values.reorder_point) : null,
        safety_stock_qty: values.safety_stock_qty !== '' ? Number(values.safety_stock_qty) : null,
        max_stock_level: values.max_stock_level !== '' ? Number(values.max_stock_level) : null,
        standard_cost: values.standard_cost !== '' ? Number(values.standard_cost) : null,
        abc_classification: values.abc_classification || null,
        ved_classification: values.ved_classification || null,
        procurement_method: values.procurement_method || null,
        default_cost_method: values.default_cost_method || null,
        storage_condition: values.storage_condition || null,
        gtin_14: values.gtin_14 || null,
        sfda_registration_no: values.sfda_registration_no || null,
      };

      let itemId = id || initialData?.id;
      if (isEdit) {
        const res = await erpClient.updateItem(itemId, payload);
        itemId = res?.data?.id || itemId;
        toast.success(t('Master Data Item updated successfully'));
      } else {
        const res = await erpClient.createItem(payload);
        itemId = res?.data?.id;
        toast.success(t('Master Data Item created successfully'));
      }

      navigate('/manager/erp/items');
    } catch (error) {
      console.error('Failed to save ERP Item:', error);
      const msg = error.response?.data?.message || t('Error saving item');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="pi pi-spin pi-spinner text-3xl mb-2" />
        <p>{t('Loading Item details...')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-[1600px] mx-auto">
      <PageHeader
        title={isEdit ? t('Update Master Data Item') : t('Create Master Data Item')}
        backPath="/manager/erp/items"
      />

      <Formik
        initialValues={buildValues(initialData)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => (
          <Form className="space-y-4">
            {/* General Info */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <SectionHeading icon="pi-info-circle" label={t('General Info')} />
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
                <SharedInputText label={`${t('Item Code')} *`} name="item_code" placeholder={t('Item Code')} />
                <SharedInputText label={t('Short Name')} name="short_name" placeholder={t('Short Name')} />
                <SharedInputText label={`${t('Item Name (EN)')} *`} name="name_en" placeholder={t('Item Name (EN)')} />
                <SharedInputText label={`${t('Item Name (AR)')} *`} name="name_ar" placeholder={t('Item Name (AR)')} />
                <SharedInputText label={t('Generic Name (EN)')} name="generic_name_en" placeholder={t('Generic Name (EN)')} />
                <SharedInputText label={t('Generic Name (AR)')} name="generic_name_ar" placeholder={t('Generic Name (AR)')} />
                <SharedInputText label={t('Trade Name (EN)')} name="trade_name_en" placeholder={t('Trade Name (EN)')} />
                <SharedInputText label={t('Trade Name (AR)')} name="trade_name_ar" placeholder={t('Trade Name (AR)')} />
                <SharedDropdown label={t('Category')} name="category_id" options={categoryOptions} />
                <SharedInputText label={t('Subcategory')} name="subcategory_id" placeholder={t('Subcategory')} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                <div className="flex flex-col gap-1 w-full">
                  <label className="text-xs font-semibold text-gray-700">{t('Description (EN)')}</label>
                  <textarea
                    rows={3}
                    value={values.description_en}
                    onChange={(e) => setFieldValue('description_en', e.target.value)}
                    placeholder={t('Description (EN)')}
                    className="w-full border border-gray-300 rounded-md p-2 text-xs focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <label className="text-xs font-semibold text-gray-700">{t('Description (AR)')}</label>
                  <textarea
                    rows={3}
                    value={values.description_ar}
                    onChange={(e) => setFieldValue('description_ar', e.target.value)}
                    placeholder={t('Description (AR)')}
                    dir="rtl"
                    className="w-full border border-gray-300 rounded-md p-2 text-xs focus:outline-none focus:border-primary text-right"
                  />
                </div>
              </div>
            </div>

            {/* Classification */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <SectionHeading icon="pi-sitemap" label={t('Classification')} />
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <SharedDropdown label={`${t('Item Type')} *`} name="item_type" options={ITEM_TYPE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))} />
                <SharedDropdown label={`${t('ABC Classification')} *`} name="abc_classification" options={ABC_CLASSIFICATION_OPTIONS} />
                <SharedDropdown label={`${t('VED Classification')} *`} name="ved_classification" options={VED_CLASSIFICATION_OPTIONS} />
                <SharedInputText label={t('Therapeutic Class')} name="therapeutic_class" placeholder={t('Therapeutic Class')} />
                <SharedInputText label={t('Drug Form')} name="drug_form" placeholder={t('Drug Form')} />
                <SharedInputText label={t('Route of Administration')} name="route_of_administration" placeholder={t('Route of Administration')} />
                <SharedInputText label={t('Strength')} name="strength" placeholder="500mg" />
                <SharedInputText label={t('Strength UOM')} name="strength_uom" placeholder="mg" />
                <SharedDropdown label={`${t('Stock UOM ID')} *`} name="stock_uom_id" options={uomOptions} />
              </div>
            </div>

            {/* Storage & Tracking */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <SectionHeading icon="pi-exclamation-triangle" label={t('Storage & Tracking')} />
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <SharedDropdown label={t('Storage Condition')} name="storage_condition" options={[{ value: '', label: t('Select Option') }, ...STORAGE_CONDITION_OPTIONS]} />
                <SharedInputText label={t('GTIN-14')} name="gtin_14" placeholder="14 numeric digits" />
                <SharedInputText label={t('SFDA Registration No')} name="sfda_registration_no" placeholder={t('SFDA registration number')} />
                <SharedInputText label={t('Min Temp (°C)')} name="storage_temp_min" type="number" placeholder="15" />
                <SharedInputText label={t('Max Temp (°C)')} name="storage_temp_max" type="number" placeholder="30" />
                <SharedDropdown label={t('Batch Tracked')} name="is_batch_tracked" options={YES_NO_OPTIONS} />
                <SharedDropdown label={t('Expiry Tracked')} name="is_expiry_tracked" options={YES_NO_OPTIONS} />
                <SharedDropdown label={t('Quality Test Required')} name="quality_test_required" options={YES_NO_OPTIONS} />
                <SharedDropdown label={t('Active')} name="is_active" options={YES_NO_OPTIONS} />
              </div>
            </div>

            {/* Inventory & Cost */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <SectionHeading icon="pi-dollar" label={t('Inventory & Cost')} />
              <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
                <SharedInputText label={t('Min Order Qty')} name="min_order_qty" type="number" placeholder="10" />
                <SharedInputText label={t('Reorder Point')} name="reorder_point" type="number" placeholder="100" />
                <SharedInputText label={t('Safety Stock')} name="safety_stock_qty" type="number" placeholder="50" />
                <SharedInputText label={t('Max Stock Level')} name="max_stock_level" type="number" placeholder="1000" />
                <SharedDropdown label={t('Procurement Method')} name="procurement_method" options={[{ value: '', label: t('Select Option') }, ...PROCUREMENT_METHOD_OPTIONS]} />
                <SharedDropdown label={t('Default Cost Method')} name="default_cost_method" options={[{ value: '', label: t('Select Option') }, ...DEFAULT_COST_METHOD_OPTIONS]} />
                <SharedInputText label={t('Standard Cost')} name="standard_cost" type="number" step="0.01" placeholder="2.5" />
                <SharedDropdown label={t('Is Purchased')} name="is_purchased" options={YES_NO_OPTIONS} />
              </div>
            </div>

            {/* Packaging / UOM */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <SectionHeading icon="pi-box" label={t('Packaging / UOM')} />
              {isEdit ? (
                <ItemPackagingTab itemId={id || initialData?.id} item={initialData} />
              ) : (
                <p className="text-xs text-gray-500">
                  {t('Save the item first. Its base UOM is created automatically, then packaging levels can be added.')}
                </p>
              )}
            </div>

            {errors && Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-md p-2">
                {t('Please fix the highlighted fields before saving.')}
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex justify-end gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
              <SharedButton
                type="button"
                label={t('Cancel')}
                icon="pi pi-times"
                buttonType="reset"
                onClick={() => navigate('/manager/erp/items')}
              />
              <SharedButton
                type="submit"
                label={isSubmitting ? t('Saving...') : isEdit ? t('Update') : t('Create')}
                icon="pi pi-check"
                buttonType="primary"
                loading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}